import { useCallback, useEffect, useRef, useState } from "react";
import storage from "/src/utils/storage";
import { BEHAVIORAL_FLAGS, DIMENSIONS } from "../data/scorecard";
import { buildMockEvaluations } from "../data/mockEvaluations";
import { buildEvaluationRecord, getMissingItems, suggestLeakage } from "../utils/scoring";

// No backend yet: the in-progress notepad (draft) and the submitted
// evaluations (log) live in localStorage. Swapping this hook's persistence
// for API calls is the only change needed once an endpoint exists.
const DRAFT_KEY = "call_evaluation_draft";
const LOG_KEY = "call_evaluation_log";

const today = () => new Date().toISOString().slice(0, 10);

export const createEmptyDraft = () => ({
    info: { agent: "", manager: "", date: today(), callId: "", team: "", geo: "", duration: "", outcome: "" },
    scores: Object.fromEntries(DIMENSIONS.map((dimension) => [dimension.id, null])),
    evidence: {},
    coaching: {},
    flags: Object.fromEntries(BEHAVIORAL_FLAGS.map((flag) => [flag.id, { observed: "", notes: "" }])),
    summary: {
        leakage: "", objection: "", urgency: "", firstCloseMin: "", closeAttempts: "",
        priorityShift: "", bestMoment: "", coachingPriority: "",
    },
});

function loadDraft() {
    const base = createEmptyDraft();
    const stored = storage("local").get(DRAFT_KEY);
    if (!stored || typeof stored !== "object") return base;
    return {
        ...base,
        ...stored,
        info: { ...base.info, ...stored.info },
        scores: { ...base.scores, ...stored.scores },
        flags: { ...base.flags, ...stored.flags },
        summary: { ...base.summary, ...stored.summary },
    };
}

function loadLog() {
    const stored = storage("local").get(LOG_KEY);
    return Array.isArray(stored) && stored.length ? stored : buildMockEvaluations();
}

function useCallEvaluation() {
    const [draft, setDraft] = useState(loadDraft);
    const [log, setLog] = useState(loadLog);
    const [savedAt, setSavedAt] = useState(null);
    const isFirstRender = useRef(true);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return undefined;
        }
        const timer = setTimeout(() => {
            storage("local").set(DRAFT_KEY, draft);
            setSavedAt(new Date());
        }, 400);
        return () => clearTimeout(timer);
    }, [draft]);

    useEffect(() => {
        storage("local").set(LOG_KEY, log);
    }, [log]);

    const updateInfo = useCallback((field, value) => {
        setDraft((prev) => ({ ...prev, info: { ...prev.info, [field]: value } }));
    }, []);

    // Clicking the already-selected score clears it (handy mid-call).
    const setScore = useCallback((id, value) => {
        setDraft((prev) => ({ ...prev, scores: { ...prev.scores, [id]: prev.scores[id] === value ? null : value } }));
    }, []);

    const setNote = useCallback((kind, id, value) => {
        setDraft((prev) => ({ ...prev, [kind]: { ...prev[kind], [id]: value } }));
    }, []);

    const setFlag = useCallback((id, patch) => {
        setDraft((prev) => ({ ...prev, flags: { ...prev.flags, [id]: { ...prev.flags[id], ...patch } } }));
    }, []);

    const updateSummary = useCallback((field, value) => {
        setDraft((prev) => ({ ...prev, summary: { ...prev.summary, [field]: value } }));
    }, []);

    const resetDraft = useCallback(() => setDraft(createEmptyDraft()), []);

    const submit = useCallback(() => {
        const missing = getMissingItems(draft);
        if (missing.length) return { ok: false, missing };

        const record = buildEvaluationRecord(draft);
        record.leakage = record.leakage || suggestLeakage(draft.scores);

        setLog((prev) => [record, ...prev]);
        setDraft(createEmptyDraft());
        return { ok: true, record };
    }, [draft]);

    const resetDemoData = useCallback(() => setLog(buildMockEvaluations()), []);

    return {
        draft, log, savedAt,
        updateInfo, setScore, setNote, setFlag, updateSummary,
        resetDraft, submit, resetDemoData,
    };
}

export default useCallEvaluation;
